import AjaxService from '@services/AjaxService'
import UrlParameterInterface from '../UrlParameterInterface'

export interface SelectOption<T = number | string> {
  text: string
  value: T
}

export interface SelectOptionGroup<T = number | string> {
  optionGroup: string
  options: SelectOption<T>[]
}

interface FlatLegacyOption {
  key: string
  value: string
}

interface GroupLegacyOption {
  key: string
  value: FlatLegacyOption[]
}

interface OptionsResponse {
  options: Array<FlatLegacyOption | GroupLegacyOption>
}

export default abstract class BaseRepository {
  protected readonly ajaxService: AjaxService
  protected abstract baseUrl: string

  public constructor(ajaxService: AjaxService) {
    this.ajaxService = ajaxService
  }

  public loadLegacySelectOptions<T = number | string>(
    type: string,
    urlParameters: UrlParameterInterface = {}
  ): Promise<Array<SelectOption<T> | SelectOptionGroup<T>>> {
    const url = `/de/Portal/ajax/dropdown/contentType/${type}/format/json`

    return this.ajaxService
      .getDropdownOptionsLegacy(url, urlParameters)
      .then((response: OptionsResponse) => {
        const options = response.options

        if (options.length > 0 && Array.isArray((options[0] as GroupLegacyOption).value)) {
          return (options as GroupLegacyOption[]).map(group => ({
            optionGroup: group.key,
            options: group.value.map(opt => {
              const number = parseInt(opt.key, 10)
              const value = isNaN(number) ? (opt.key as any) : (number as any)
              return {value, text: opt.value} as SelectOption<T>
            }),
          }))
        }

        return (options as FlatLegacyOption[]).map(opt => {
          const number = parseInt(opt.key, 10)
          const value = isNaN(number) ? (opt.key as any) : (number as any)
          return {value, text: opt.value} as SelectOption<T>
        })
      })
  }

  /** Method can be used to load select options from a given URL. Backend should return a JSON response
   *  with a data array containing objects with id and label attributes.
   *  This can be done by using the SelectOptionTransformer in the backend.
   */
  public async loadSelectOptions(url: string, urlParameters: UrlParameterInterface = {}): Promise<Array<SelectOption>> {
    return this.ajaxService.getJsonWithParameters(url, urlParameters)
      .then((response: any) => {
        const options: SelectOption[] = [];

        for (const optionData of response.data) {
          options.push({
            value: parseInt(optionData.id, 10),
            text: optionData.attributes.label,
          })
        }
        return options;
      });
  }
}
